import { useState, useEffect } from "react";
import { canDo } from "@shared/roles";
import { useRobots, useCreateRobot, useUpdateRobot, useDeleteRobot } from "@/hooks/use-inventory";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/status-badge";
import { Plus, Search, Bot, QrCode, Eye, Pencil, Camera, Loader2, ImageIcon, Trash2, History, MapPin, Clock, ExternalLink, Wrench, CheckCircle2, LifeBuoy, ShieldAlert } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link, useLocation } from "wouter";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { authFetch } from "@/lib/authFetch";
import { useActiveRobotLoan, useCloseRobotLoan } from "@/hooks/use-client-robot-assignments";
import { useClients } from "@/hooks/use-clients";
import { PhotoUploadSlot } from "@/components/photo-upload-slot";
import { supabase } from "@/lib/supabase";
import type { Robot } from "@shared/schema";
import {
  getRobotFleetStatus,
  ROBOT_FLEET_ASSIGNABLE_STATUSES,
  ROBOT_FLEET_STATUS_VALUES,
} from "@shared/robotStatusConfig";

const categoryLabels: Record<string, string> = {
  livraison: "Livraison",
  nettoyage: "Nettoyage",
  industriel: "Industriel",
};

const typeLabels: Record<string, string> = {
  demo: "Démo",
  vendu: "Vendu",
  client: "Client",
};

const typeBadgeClass: Record<string, string> = {
  demo: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  client: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400",
  vendu: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
};

const identityPhotoSlots = [
  { type: "front", label: "Face avant", urlKey: "photoFrontUrl" },
  { type: "back", label: "Face arrière", urlKey: "photoBackUrl" },
  { type: "left", label: "Côté gauche", urlKey: "photoLeftUrl" },
  { type: "right", label: "Côté droit", urlKey: "photoRightUrl" },
] as const;

const STATUT_ATELIER_LABELS: Record<string, string> = {
  disponible: "Disponible",
  indisponible: "Indisponible",
  en_demo: "En démonstration",
  en_location: "En location",
  en_pret_client: "Prêt client",
  a_reparer: "À réparer",
  en_reparation: "En réparation",
  hors_service: "Hors service",
};

const STATUT_ATELIER_COLORS: Record<string, string> = {
  disponible: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  indisponible: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  en_demo: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  en_location: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
  en_pret_client: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400",
  a_reparer: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  en_reparation: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  hors_service: "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400",
};


export default function RobotList() {
  const { profile } = useAuth();
  const { data: robots, isLoading } = useRobots();
  const { data: clients = [] } = useClients();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedRobot, setSelectedRobot] = useState<Robot | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState<Record<string, boolean>>({});
  const [typeFilter, setTypeFilter] = useState<"all" | "demo" | "client" | "vendu">("all");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createMutation = useCreateRobot();
  const updateMutation = useUpdateRobot();
  const deleteMutation = useDeleteRobot();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [detailTab, setDetailTab] = useState("infos");
  const [robotToDelete, setRobotToDelete] = useState<Robot | null>(null);

  const canEdit = canDo(profile?.accessRole) || profile?.role === 'planification';
  const [location, setLocation] = useLocation();

  const [currentStatutAtelier, setCurrentStatutAtelier] = useState<string>("disponible");
  const [arriveeOpen, setArriveeOpen] = useState(false);
  const [arriveeData, setArriveeData] = useState({ provenance: "", note: "" });
  const [arriveeLoading, setArriveeLoading] = useState(false);
  const { data: activeLoan, isLoading: activeLoanLoading } = useActiveRobotLoan(selectedRobot?.id || null, detailOpen);
  const closeLoanMutation = useCloseRobotLoan();
  const activeLoanClient = activeLoan ? (clients as any[]).find((client: any) => client.id === activeLoan.clientId) : null;

  // Sync statut atelier when selected robot changes
  useEffect(() => {
    if (selectedRobot) {
      setCurrentStatutAtelier(selectedRobot.statutAtelier || "disponible");
    }
  }, [selectedRobot?.id]);

  // Handle URL query params from barcode scanner navigation
  // LOT 2 : dépend aussi de `location` pour réagir si la page est déjà montée
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openRobotId = params.get("openRobot");
    const createWithSn = params.get("createWithSn");
    if (openRobotId && robots && (robots as Robot[]).length > 0) {
      const robot = (robots as Robot[]).find((r) => r.id === openRobotId);
      if (robot) {
        openDetail(robot);
        window.history.replaceState(null, "", "/robots");
      }
    }
    if (createWithSn) {
      setFormData((prev) => ({ ...prev, serialNumber: createWithSn, isFromScanner: true }));
      setCreateOpen(true);
      window.history.replaceState(null, "", "/robots");
    }
  }, [robots, location]);

  const handleStatutAtelierChange = async (newStatut: string) => {
    if (!selectedRobot) return;
    const previous = currentStatutAtelier;
    setCurrentStatutAtelier(newStatut);
    try {
      const res = await authFetch(`/api/robots/${selectedRobot.id}/statut`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ statutAtelier: newStatut }),
      });
      if (!res.ok) throw new Error();
      // Log the event
      await authFetch(`/api/robots/${selectedRobot.id}/events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "statut_change", ancienStatut: previous, nouveauStatut: newStatut }),
      });
      setSelectedRobot((prev) => prev ? { ...prev, statutAtelier: newStatut } : prev);
      queryClient.invalidateQueries({ queryKey: ["/api/robots"] });
      toast({ title: "Statut atelier mis à jour" });
    } catch {
      setCurrentStatutAtelier(previous);
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de mettre à jour le statut" });
    }
  };

  const handleArriveeAtelier = async () => {
    if (!selectedRobot) return;
    setArriveeLoading(true);
    try {
      await authFetch(`/api/robots/${selectedRobot.id}/statut`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ statutAtelier: "disponible", provenanceActuelle: arriveeData.provenance }),
      });
      await authFetch(`/api/robots/${selectedRobot.id}/events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "arrivee_atelier",
          nouveauStatut: "disponible",
          provenance: arriveeData.provenance,
          note: arriveeData.note,
        }),
      });
      setCurrentStatutAtelier("disponible");
      setSelectedRobot((prev) =>
        prev ? { ...prev, statutAtelier: "disponible", provenanceActuelle: arriveeData.provenance } : prev
      );
      queryClient.invalidateQueries({ queryKey: ["/api/robots"] });
      queryClient.invalidateQueries({ queryKey: [`/api/robots/${selectedRobot.id}/events`] });
      setArriveeOpen(false);
      setArriveeData({ provenance: "", note: "" });
      toast({ title: "Arrivée enregistrée", description: "Le robot est marqué comme arrivé à l'atelier." });
    } catch {
      toast({ variant: "destructive", title: "Erreur", description: "Impossible d'enregistrer l'arrivée." });
    } finally {
      setArriveeLoading(false);
    }
  };

  const handleCloseLoan = async () => {
    if (!selectedRobot || !activeLoan) return;
    try {
      await closeLoanMutation.mutateAsync({ robotId: selectedRobot.id, locationId: activeLoan.id });
      setCurrentStatutAtelier("disponible");
      setSelectedRobot(prev => prev ? {
        ...prev,
        statutAtelier: "disponible",
        status: ROBOT_FLEET_STATUS_VALUES.DISPONIBLE,
      } : prev);
      toast({ title: "Prêt clôturé", description: "Le robot est de nouveau disponible dans la flotte." });
    } catch (error: any) {
      toast({ variant: "destructive", title: "Clôture impossible", description: error.message || "Le prêt n'a pas pu être clôturé." });
    }
  };

  const [formData, setFormData] = useState({
    modelName: "",
    productModel: "",
    nickname: "",
    color: "",
    sku: "",
    serialNumber: "",
    macAddress: "",
    category: "livraison",
    robotType: "demo",
    status: ROBOT_FLEET_STATUS_VALUES.DISPONIBLE as string,
    notes: "",
    localisationActuelle: "",
    localisationDetail: "",
    isFromScanner: false,
  });
  const [skuQuery, setSkuQuery] = useState("");
  const [skuDropdownOpen, setSkuDropdownOpen] = useState(false);
  const { data: skuResults = [] } = useQuery<any[]>({
    queryKey: ["/api/products/search", skuQuery],
    queryFn: async () => {
      if (!skuQuery || skuQuery.length < 2) return [];
      const res = await authFetch(`/api/products/search?q=${encodeURIComponent(skuQuery)}`);
      return res.ok ? res.json() : [];
    },
    enabled: skuQuery.length >= 2,
  });

  const [editData, setEditData] = useState({
    modelName: "",
    productModel: "",
    nickname: "",
    color: "",
    sku: "",
    serialNumber: "",
    macAddress: "",
    category: "livraison",
    robotType: "demo",
    status: ROBOT_FLEET_STATUS_VALUES.DISPONIBLE as string,
    notes: "",
  });

  const filteredRobots = robots?.filter((robot: Robot) => {
    const matchSearch =
      robot.modelName.toLowerCase().includes(search.toLowerCase()) ||
      robot.serialNumber.toLowerCase().includes(search.toLowerCase()) ||
      robot.sku.toLowerCase().includes(search.toLowerCase()) ||
      (robot.nickname && robot.nickname.toLowerCase().includes(search.toLowerCase())) ||
      (robot.productModel && robot.productModel.toLowerCase().includes(search.toLowerCase()));
    const matchType = typeFilter === "all" || robot.robotType === typeFilter;
    return matchSearch && matchType;
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const { isFromScanner, localisationActuelle, localisationDetail, ...rest } = formData;
    const payload: any = {
      ...rest,
      macAddress: rest.macAddress || null,
      localisationActuelle: localisationActuelle || null,
      localisationDetail: localisationDetail || null,
    };
    try {
      const newRobot = await createMutation.mutateAsync(payload as any);
      // LOT 3 : logger un événement creation_via_scan si ouvert depuis le scanner
      if (isFromScanner && newRobot?.id) {
        await authFetch(`/api/robots/${newRobot.id}/events`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: "creation_via_scan",
            note: `Fiche créée suite au scan code-barres${localisationActuelle ? ` · Localisation: ${localisationActuelle}${localisationDetail ? ` (${localisationDetail})` : ""}` : ""}`,
          }),
        });
      }
      setCreateOpen(false);
      toast({ title: "Robot ajouté", description: "Le robot a été enregistré dans la flotte" });
      setFormData({ modelName: "", productModel: "", nickname: "", color: "", sku: "", serialNumber: "", macAddress: "", category: "livraison", robotType: "demo", status: ROBOT_FLEET_STATUS_VALUES.DISPONIBLE, notes: "", localisationActuelle: "", localisationDetail: "", isFromScanner: false });
      setSkuQuery("");
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err?.message || "Impossible de créer le robot" });
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRobot) return;
    try {
      await updateMutation.mutateAsync({ id: selectedRobot.id, ...editData } as any);
      setEditOpen(false);
      toast({ title: "Robot modifié", description: "Les informations ont été mises à jour" });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err?.message || "Impossible de modifier le robot" });
    }
  };

  const uploadModelImage = async (robotId: string, file: File) => {
    const key = `${robotId}_model`;
    setUploadingPhotos(prev => ({ ...prev, [key]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const res = await fetch(`/api/robots/${robotId}/model-image`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Upload échoué");
      }
      const data = await res.json();
      if (selectedRobot && selectedRobot.id === robotId) {
        setSelectedRobot(prev => prev ? { ...prev, imageUrl: data.url } : prev);
      }
      toast({ title: "Photo du modèle mise à jour" });
      queryClient.invalidateQueries({ queryKey: ['/api/robots'] });
      queryClient.invalidateQueries({ queryKey: ['/api/robot-model-images'] });
    } catch (e: any) {
      toast({ variant: "destructive", title: "Erreur upload", description: e.message });
    } finally {
      setUploadingPhotos(prev => ({ ...prev, [key]: false }));
    }
  };

  const uploadRobotPhoto = async (robotId: string, photoType: string, file: File) => {
    const key = `${robotId}_${photoType}`;
    setUploadingPhotos(prev => ({ ...prev, [key]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const res = await fetch(`/api/robots/${robotId}/photos/${photoType}`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Upload échoué");
      }
      const data = await res.json();
      const urlKeyMap: Record<string, string> = {
        profile: "profilePhotoUrl",
        front: "photoFrontUrl",
        back: "photoBackUrl",
        left: "photoLeftUrl",
        right: "photoRightUrl",
      };
      if (selectedRobot && selectedRobot.id === robotId) {
        setSelectedRobot(prev => prev ? { ...prev, [urlKeyMap[photoType]]: data.url } : prev);
      }
      toast({ title: "Photo uploadée" });
      queryClient.invalidateQueries({ queryKey: ['/api/robots'] });
    } catch (e: any) {
      toast({ variant: "destructive", title: "Erreur upload", description: e.message });
    } finally {
      setUploadingPhotos(prev => ({ ...prev, [key]: false }));
    }
  };

  const openDetail = (robot: Robot) => {
    setSelectedRobot(robot);
    setDetailTab("infos");
    setDetailOpen(true);
  };

  const { data: robotLocationsData, isLoading: locationsLoading } = useQuery<any[]>({
    queryKey: ["/api/robots", selectedRobot?.id, "locations"],
    queryFn: () => authFetch(`/api/robots/${selectedRobot!.id}/locations`).then(r => r.json()),
    enabled: !!selectedRobot && detailTab === "historique",
  });

  const { data: robotEventsData, isLoading: eventsLoading } = useQuery<any[]>({
    queryKey: ["/api/robots", selectedRobot?.id, "events"],
    queryFn: () => authFetch(`/api/robots/${selectedRobot!.id}/events`).then(r => r.json()),
    enabled: !!selectedRobot && detailTab === "historique",
  });

  const { data: robotSavDossiers = [], isLoading: savDossiersLoading } = useQuery<any[]>({
    queryKey: ["/api/robots", selectedRobot?.id, "sav"],
    queryFn: () => authFetch(`/api/robots/${selectedRobot!.id}/sav`).then(r => r.json()),
    enabled: !!selectedRobot && detailTab === "sav",
  });

  const { data: snSearchResults = [] } = useQuery<any[]>({
    queryKey: ["/api/sav/recherche-robots", selectedRobot?.serialNumber],
    queryFn: () => authFetch(`/api/sav/recherche-robots?sn=${encodeURIComponent(selectedRobot!.serialNumber || '')}`).then(r => r.json()),
    enabled: !!selectedRobot?.serialNumber && detailTab === "sav",
  });
  const puduContext = snSearchResults.find((r: any) => r.present_dans?.pudu_robots)?.pudu ?? null;

  const openEdit = (robot: Robot) => {
    setSelectedRobot(robot);
    setEditData({
      modelName: robot.modelName,
      productModel: robot.productModel || "",
      nickname: robot.nickname || "",
      color: robot.color || "",
      sku: robot.sku,
      serialNumber: robot.serialNumber,
      macAddress: robot.macAddress,
      category: robot.category,
      robotType: robot.robotType,
      status: robot.status,
      notes: robot.notes || "",
    });
    setEditOpen(true);
  };

  const confirmDelete = (robot: Robot) => {
    setRobotToDelete(robot);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = () => {
    if (!robotToDelete) return;
    deleteMutation.mutate(robotToDelete.id, {
      onSuccess: () => {
        toast({ title: "Robot supprimé", description: `${robotToDelete.modelName} a été supprimé avec succès.` });
        setDeleteConfirmOpen(false);
        setRobotToDelete(null);
        if (selectedRobot?.id === robotToDelete.id) {
          setDetailOpen(false);
          setSelectedRobot(null);
        }
      },
      onError: (err: any) => {
        toast({ variant: "destructive", title: "Erreur", description: err.message || "Impossible de supprimer le robot" });
      },
    });
  };

  const generateQrCode = async (robot: Robot) => {
    setQrLoading(true);
    try {
      const res = await authFetch(`/api/robots/${robot.id}/qrcode`, { method: "POST" });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSelectedRobot({ ...robot, qrCodeUrl: data.qrCodeUrl });
      toast({ title: "QR Code généré", description: "Le QR code a été créé pour ce robot" });
    } catch {
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de générer le QR code" });
    } finally {
      setQrLoading(false);
    }
  };

  const stats = {
    total: robots?.length || 0,
    disponible: robots?.filter((r: Robot) => getRobotFleetStatus(r.status).counterCategory === "disponible").length || 0,
    en_mission: robots?.filter((r: Robot) => getRobotFleetStatus(r.status).counterCategory === "en_mission").length || 0,
    en_panne: robots?.filter((r: Robot) => getRobotFleetStatus(r.status).counterCategory === "en_panne").length || 0,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">Robots</h1>
          <p className="text-muted-foreground mt-1">Gestion de la flotte de robots</p>
        </div>
        {canEdit && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="shadow-lg shadow-primary/20" data-testid="button-add-robot">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Robot
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[550px]">
              <DialogHeader>
                <DialogTitle>Nouveau Robot</DialogTitle>
                <DialogDescription>Enregistrer un nouveau robot dans la flotte.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nom du modèle</Label>
                    <Input data-testid="input-model-name" required value={formData.modelName} onChange={e => setFormData({...formData, modelName: e.target.value})} placeholder="Ex: BellaBot" />
                  </div>
                  <div className="space-y-2">
                    <Label>Modèle de produit</Label>
                    <Input data-testid="input-product-model" value={formData.productModel} onChange={e => setFormData({...formData, productModel: e.target.value})} placeholder="Ex: BellaBot Pro" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Surnom de la machine</Label>
                    <Input data-testid="input-nickname" value={formData.nickname} onChange={e => setFormData({...formData, nickname: e.target.value})} placeholder="Ex: Bella-01" />
                  </div>
                  <div className="space-y-2">
                    <Label>Couleur</Label>
                    <Input data-testid="input-color" value={formData.color} onChange={e => setFormData({...formData, color: e.target.value})} placeholder="Ex: Blanc" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {/* LOT 3 — SKU autocomplete sur la base produits robots */}
                  <div className="space-y-2 relative">
                    <Label>SKU <span className="text-muted-foreground font-normal">(autocomplete)</span></Label>
                    <Input
                      data-testid="input-sku"
                      required
                      value={skuQuery || formData.sku}
                      onChange={e => {
                        setSkuQuery(e.target.value);
                        setFormData({...formData, sku: e.target.value});
                        setSkuDropdownOpen(true);
                      }}
                      onBlur={() => setTimeout(() => setSkuDropdownOpen(false), 180)}
                      onFocus={() => skuQuery.length >= 2 && setSkuDropdownOpen(true)}
                      placeholder="CC1, BellaBot, MT1…"
                    />
                    {skuDropdownOpen && skuResults.length > 0 && (
                      <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-lg max-h-44 overflow-y-auto">
                        {skuResults.map((p: any) => (
                          <button
                            key={p.id}
                            type="button"
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent text-left"
                            onMouseDown={() => {
                              setSkuQuery(p.sku || p.nomFr);
                              setFormData({...formData,
                                sku: p.sku || "",
                                modelName: formData.modelName || p.nomFr || "",
                                productModel: formData.productModel || p.nomFr || "",
                              });
                              setSkuDropdownOpen(false);
                            }}
                          >
                            {p.imageUrl && <img src={p.imageUrl} className="w-6 h-6 object-contain rounded flex-shrink-0" alt="" />}
                            <span className="font-medium truncate">{p.nomFr}</span>
                            {p.sku && <span className="text-muted-foreground text-xs ml-auto flex-shrink-0">{p.sku}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Numéro de série</Label>
                    <Input data-testid="input-serial" required value={formData.serialNumber} onChange={e => setFormData({...formData, serialNumber: e.target.value})} placeholder="SN-..." />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {/* LOT 3 — MAC optionnelle */}
                  <div className="space-y-2">
                    <Label>Adresse MAC <span className="text-muted-foreground font-normal text-xs">(optionnel)</span></Label>
                    <Input data-testid="input-mac" value={formData.macAddress} onChange={e => setFormData({...formData, macAddress: e.target.value})} placeholder="00:00:00:..." />
                    <p className="text-xs text-muted-foreground">Peut être complétée plus tard</p>
                  </div>
                  {/* LOT 3 — Localisation actuelle */}
                  <div className="space-y-2">
                    <Label>Localisation actuelle</Label>
                    <Select value={formData.localisationActuelle} onValueChange={v => setFormData({...formData, localisationActuelle: v, localisationDetail: ""})}>
                      <SelectTrigger data-testid="select-localisation"><SelectValue placeholder="Où se trouve ce robot ?" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="massy_bureau">Massy — Bureau</SelectItem>
                        <SelectItem value="massy_stock">Massy — Stock</SelectItem>
                        <SelectItem value="showroom">Showroom</SelectItem>
                        <SelectItem value="autre">Autre (préciser)</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.localisationActuelle === "autre" && (
                      <Input
                        placeholder="Ex: En route, Chez client…"
                        value={formData.localisationDetail}
                        onChange={e => setFormData({...formData, localisationDetail: e.target.value})}
                        data-testid="input-localisation-detail"
                      />
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Catégorie</Label>
                    <Select value={formData.category} onValueChange={v => setFormData({...formData, category: v})}>
                      <SelectTrigger data-testid="select-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="livraison">Livraison</SelectItem>
                        <SelectItem value="nettoyage">Nettoyage</SelectItem>
                        <SelectItem value="industriel">Industriel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={formData.robotType} onValueChange={v => setFormData({...formData, robotType: v})}>
                      <SelectTrigger data-testid="select-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="demo">Démo</SelectItem>
                        <SelectItem value="vendu">Vendu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea data-testid="input-notes" value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} placeholder="Notes optionnelles..." rows={2} />
                </div>
                <div className="pt-4 flex justify-end gap-2">
                  <Button variant="outline" type="button" onClick={() => setCreateOpen(false)}>Annuler</Button>
                  <Button type="submit" disabled={createMutation.isPending} data-testid="button-submit-robot">
                    {createMutation.isPending ? "Création..." : "Créer le robot"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl border p-4 shadow-sm">
          <p className="text-sm text-muted-foreground">Total</p>
          <p className="text-2xl font-bold" data-testid="text-stat-total">{stats.total}</p>
        </div>
        <div className="bg-card rounded-xl border p-4 shadow-sm">
          <p className="text-sm text-emerald-600">Disponibles</p>
          <p className="text-2xl font-bold text-emerald-600" data-testid="text-stat-available">{stats.disponible}</p>
        </div>
        <div className="bg-card rounded-xl border p-4 shadow-sm">
          <p className="text-sm text-amber-600">En mission</p>
          <p className="text-2xl font-bold text-amber-600" data-testid="text-stat-mission">{stats.en_mission}</p>
        </div>
        <div className="bg-card rounded-xl border p-4 shadow-sm">
          <p className="text-sm text-rose-600">En panne / réparation</p>
          <p className="text-2xl font-bold text-rose-600" data-testid="text-stat-broken">{stats.en_panne}</p>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-4 bg-card p-2 rounded-xl border shadow-sm flex-1 min-w-64">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              data-testid="input-search-robots"
              placeholder="Rechercher par modèle, série ou SKU..." 
              className="pl-9 border-none bg-transparent focus-visible:ring-0"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="flex items-center gap-1.5 bg-card p-1.5 rounded-xl border shadow-sm">
          {([
            { val: "all", label: "Tous" },
            { val: "demo", label: "Démo" },
            { val: "client", label: "Client" },
            { val: "vendu", label: "Vendu" },
          ] as const).map(({ val, label }) => (
            <button
              key={val}
              data-testid={`filter-type-${val}`}
              onClick={() => setTypeFilter(val)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === val
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-secondary"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead>Robot</TableHead>
              <TableHead>Catégorie</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center h-32">Chargement...</TableCell>
              </TableRow>
            ) : filteredRobots?.map((robot: Robot) => (
              <TableRow key={robot.id} className="group hover:bg-secondary/20 transition-colors" data-testid={`row-robot-${robot.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {(robot.imageUrl || robot.profilePhotoUrl) ? (
                      <img src={robot.imageUrl || robot.profilePhotoUrl || ''} alt={robot.modelName} className="w-10 h-10 rounded-lg object-cover border shadow-sm" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center border shadow-sm">
                        <Bot className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                    )}
                    <div>
                      <div className="font-semibold text-foreground">{robot.modelName}</div>
                      {robot.productModel && <div className="text-xs text-muted-foreground">{robot.productModel}</div>}
                      <div className="text-xs text-muted-foreground font-mono">{robot.serialNumber}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-sm">{categoryLabels[robot.category] || robot.category}</span>
                </TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded-md text-xs font-medium uppercase tracking-wide ${typeBadgeClass[robot.robotType] ?? "bg-secondary"}`}>
                    {typeLabels[robot.robotType] || robot.robotType}
                  </span>
                </TableCell>
                <TableCell>
                  <StatusBadge status={robot.status} type="robot" />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" onClick={() => openDetail(robot)} data-testid={`button-view-robot-${robot.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    {canEdit && (
                      <Button variant="ghost" size="sm" onClick={() => openEdit(robot)} data-testid={`button-edit-robot-${robot.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    {canEdit && (
                      <Button variant="ghost" size="sm" onClick={() => confirmDelete(robot)} data-testid={`button-delete-robot-${robot.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && (!filteredRobots || filteredRobots.length === 0) && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Aucun robot trouvé</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-primary" />
              Fiche Robot — {selectedRobot?.modelName}
            </DialogTitle>
          </DialogHeader>
          {selectedRobot && (
            <Tabs value={detailTab} onValueChange={setDetailTab} className="pt-2">
              <TabsList className="grid grid-cols-3 w-full mb-4">
                <TabsTrigger value="infos" data-testid="tab-infos">Informations</TabsTrigger>
                <TabsTrigger value="historique" data-testid="tab-historique">
                  <History className="w-3.5 h-3.5 mr-1.5" />
                  Historique
                </TabsTrigger>
                <TabsTrigger value="sav" data-testid="tab-sav">
                  <ShieldAlert className="w-3.5 h-3.5 mr-1.5" />
                  SAV
                </TabsTrigger>
              </TabsList>

              <TabsContent value="infos" className="space-y-6 mt-0">
              {/* Bloc Statut Atelier */}
              <div className="rounded-xl border bg-secondary/30 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-primary" />
                    Statut atelier
                  </p>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${STATUT_ATELIER_COLORS[currentStatutAtelier] || "bg-secondary"}`} data-testid="badge-statut-atelier">
                    {STATUT_ATELIER_LABELS[currentStatutAtelier] || currentStatutAtelier || "Non défini"}
                  </span>
                </div>
                {canEdit && (
                  <Select value={currentStatutAtelier} onValueChange={handleStatutAtelierChange}>
                    <SelectTrigger className="h-9 text-sm" data-testid="select-statut-atelier">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUT_ATELIER_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {selectedRobot.provenanceActuelle && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <MapPin className="w-3 h-3 shrink-0" />
                    Provenance : <span className="font-medium">{selectedRobot.provenanceActuelle}</span>
                  </p>
                )}
                {!activeLoanLoading && activeLoan && (
                  <div className="rounded-lg border border-sky-200 bg-sky-50/70 dark:border-sky-900 dark:bg-sky-950/30 p-3 space-y-2" data-testid="active-robot-loan">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-sky-900 dark:text-sky-200">Prêt client en cours</p>
                      {canEdit && (
                        <Button variant="outline" size="sm" className="h-7 text-xs border-sky-300" onClick={handleCloseLoan} disabled={closeLoanMutation.isPending} data-testid="button-close-robot-loan">
                          {closeLoanMutation.isPending ? "Clôture..." : "Clôturer le prêt"}
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                      <div><span className="text-muted-foreground block">Client dépositaire</span><span className="font-medium">{activeLoan.clientName || activeLoan.client?.companyName || activeLoan.client?.nomCommercial || activeLoanClient?.nomCommercial || activeLoanClient?.companyName || activeLoan.clientId || "—"}</span></div>
                      <div><span className="text-muted-foreground block">Depuis le</span><span className="font-medium">{activeLoan.dateDebut ? new Date(activeLoan.dateDebut).toLocaleDateString("fr-FR") : activeLoan.startedAt ? new Date(activeLoan.startedAt).toLocaleDateString("fr-FR") : activeLoan.startDate ? new Date(activeLoan.startDate).toLocaleDateString("fr-FR") : "—"}</span></div>
                      <div><span className="text-muted-foreground block">Mission d'origine</span><span className="font-medium">{activeLoan.missionName || activeLoan.mission?.eventName || activeLoan.missionId || "—"}</span></div>
                    </div>
                    {!canEdit && <p className="text-[11px] text-muted-foreground">La clôture est réservée aux utilisateurs autorisés à modifier les robots.</p>}
                  </div>
                )}
                <div className="flex gap-2 pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setArriveeOpen(true)}
                    data-testid="button-marquer-arrive"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1.5 text-green-600" />
                    Marquer comme arrivé
                  </Button>
                  {(currentStatutAtelier === "a_reparer" || currentStatutAtelier === "en_reparation") && (
                    <Button variant="outline" size="sm" className="flex-1 border-orange-200 text-orange-600 dark:border-orange-800 dark:text-orange-400" asChild>
                      <Link href="/tickets">
                        <LifeBuoy className="w-3.5 h-3.5 mr-1.5" />
                        Tickets SAV
                      </Link>
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex flex-col gap-2 shrink-0">
                  {canEdit ? (
                    <PhotoUploadSlot
                      label="Photo du modèle"
                      currentUrl={selectedRobot.imageUrl || null}
                      uploading={!!uploadingPhotos[`${selectedRobot.id}_model`]}
                      onUpload={(file) => uploadModelImage(selectedRobot.id, file)}
                      testId="upload-model-image"
                      aspect={1}
                    />
                  ) : (
                    <div className="w-32 shrink-0">
                      {selectedRobot.imageUrl ? (
                        <img src={selectedRobot.imageUrl} alt="Modèle" className="w-full h-32 object-cover rounded-lg border" />
                      ) : (
                        <div className="w-full h-32 rounded-lg bg-secondary/50 flex items-center justify-center border">
                          <ImageIcon className="w-10 h-10 text-muted-foreground/50" />
                        </div>
                      )}
                    </div>
                  )}
                  {canEdit ? (
                    <PhotoUploadSlot
                      label="Photo de profil"
                      currentUrl={selectedRobot.profilePhotoUrl || null}
                      uploading={!!uploadingPhotos[`${selectedRobot.id}_profile`]}
                      onUpload={(file) => uploadRobotPhoto(selectedRobot.id, "profile", file)}
                      testId="upload-profile-photo"
                      aspect={1}
                    />
                  ) : selectedRobot.profilePhotoUrl ? (
                    <div className="w-32 shrink-0">
                      <img src={selectedRobot.profilePhotoUrl} alt="Profil" className="w-full h-32 object-cover rounded-lg border" />
                    </div>
                  ) : null}
                </div>
                <div className="grid grid-cols-2 gap-4 flex-1">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">SKU</p>
                    <p className="font-mono font-medium" data-testid="text-detail-sku">{selectedRobot.sku}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Numéro de série</p>
                    <p className="font-mono font-medium" data-testid="text-detail-serial">{selectedRobot.serialNumber}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Adresse MAC</p>
                    <p className="font-mono font-medium">{selectedRobot.macAddress}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Modèle de produit</p>
                    <p className="font-medium" data-testid="text-detail-product-model">{selectedRobot.productModel || "—"}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Surnom</p>
                    <p className="font-medium" data-testid="text-detail-nickname">{selectedRobot.nickname || "—"}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Couleur</p>
                    <p className="font-medium" data-testid="text-detail-color">{selectedRobot.color || "—"}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Statut</p>
                    <StatusBadge status={selectedRobot.status} type="robot" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Catégorie</p>
                    <p className="font-medium">{categoryLabels[selectedRobot.category]}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Type</p>
                    <p className="font-medium">{typeLabels[selectedRobot.robotType]}</p>
                  </div>
                </div>
              </div>

              {selectedRobot.notes && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Notes</p>
                  <p className="text-sm bg-secondary/50 rounded-lg p-3">{selectedRobot.notes}</p>
                </div>
              )}

              <div className="space-y-3">
                <p className="text-sm font-semibold flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Photos d'identité
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {identityPhotoSlots.map(({ type, label, urlKey }) => {
                    const url = selectedRobot[urlKey as keyof Robot] as string | null;
                    if (canEdit) {
                      return (
                        <PhotoUploadSlot
                          key={type}
                          label={label}
                          currentUrl={url}
                          uploading={!!uploadingPhotos[`${selectedRobot.id}_${type}`]}
                          onUpload={(file) => uploadRobotPhoto(selectedRobot.id, type, file)}
                          testId={`upload-photo-${type}`}
                        />
                      );
                    }
                    return (
                      <div key={type} className="rounded-lg border bg-secondary/30 p-3 text-center" data-testid={`photo-${urlKey}`}>
                        <p className="text-xs text-muted-foreground mb-2">{label}</p>
                        {url ? (
                          <img src={url} alt={label} className="w-full h-28 object-cover rounded-md" />
                        ) : (
                          <div className="w-full h-28 rounded-md bg-secondary/50 flex items-center justify-center">
                            <Camera className="w-8 h-8 text-muted-foreground/50" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold flex items-center gap-2">
                    <QrCode className="w-4 h-4" />
                    QR Code
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => generateQrCode(selectedRobot)}
                    disabled={qrLoading}
                    data-testid="button-generate-qr"
                  >
                    {qrLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <QrCode className="w-4 h-4 mr-2" />}
                    {selectedRobot.qrCodeUrl ? "Régénérer" : "Générer"}
                  </Button>
                </div>
                {selectedRobot.qrCodeUrl ? (
                  <div className="flex justify-center p-4 bg-white rounded-lg border">
                    <img src={selectedRobot.qrCodeUrl} alt="QR Code" className="w-48 h-48" data-testid="img-qrcode" />
                  </div>
                ) : (
                  <div className="text-center text-sm text-muted-foreground p-6 bg-secondary/30 rounded-lg">
                    Aucun QR code généré. Cliquez sur "Générer" pour créer un QR code.
                  </div>
                )}
              </div>
              </TabsContent>

              <TabsContent value="historique" className="mt-0 space-y-6">
                {/* Événements atelier */}
                <div className="space-y-3">
                  <p className="text-sm font-semibold flex items-center gap-2">
                    <Wrench className="w-4 h-4" />
                    Événements atelier
                  </p>
                  {eventsLoading ? (
                    <div className="space-y-2">{[1,2].map(i=><Skeleton key={i} className="h-12 w-full rounded-lg"/>)}</div>
                  ) : !robotEventsData || robotEventsData.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic py-2">Aucun événement enregistré pour ce robot.</p>
                  ) : (
                    <div className="space-y-2">
                      {robotEventsData.map((ev: any) => {
                        const evDate = new Date(ev.created_at);
                        return (
                          <div key={ev.id} className="flex items-start gap-3 rounded-lg border bg-card p-3 text-sm" data-testid={`event-entry-${ev.id}`}>
                            <div className="shrink-0 mt-0.5">
                              {ev.type === "arrivee_atelier" ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Arrivée</span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">Statut</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0 space-y-0.5">
                              <div className="flex items-center gap-2 flex-wrap">
                                {ev.ancien_statut && ev.nouveau_statut ? (
                                  <span className="text-xs text-muted-foreground">
                                    {STATUT_ATELIER_LABELS[ev.ancien_statut] || ev.ancien_statut} → <span className="font-medium text-foreground">{STATUT_ATELIER_LABELS[ev.nouveau_statut] || ev.nouveau_statut}</span>
                                  </span>
                                ) : ev.nouveau_statut ? (
                                  <span className="text-xs font-medium">{STATUT_ATELIER_LABELS[ev.nouveau_statut] || ev.nouveau_statut}</span>
                                ) : null}
                                {ev.provenance && (
                                  <span className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-2.5 h-2.5"/>{ev.provenance}</span>
                                )}
                              </div>
                              {ev.note && <p className="text-xs text-muted-foreground italic">{ev.note}</p>}
                              <div className="flex items-center gap-2 text-xs text-muted-foreground/60">
                                <span>{evDate.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</span>
                                {ev.user_name && <span>• {ev.user_name}</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Historique de localisation */}
                <div className="space-y-3">
                  <p className="text-sm font-semibold flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Historique de localisation
                  </p>
                {locationsLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                      <Skeleton key={i} className="h-16 w-full rounded-lg" />
                    ))}
                  </div>
                ) : !robotLocationsData || robotLocationsData.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm flex flex-col items-center gap-2">
                    <MapPin className="w-8 h-8 text-muted-foreground/40" />
                    Aucun historique de localisation enregistré.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {robotLocationsData.map((loc: any) => {
                      const startDate = new Date(loc.startedAt);
                      const endDate = loc.endedAt ? new Date(loc.endedAt) : null;
                      const durationMs = endDate ? endDate.getTime() - startDate.getTime() : Date.now() - startDate.getTime();
                      const durationDays = Math.floor(durationMs / (1000 * 60 * 60 * 24));
                      const durationHours = Math.floor((durationMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                      const durationStr = durationDays > 0
                        ? `${durationDays} jour${durationDays > 1 ? "s" : ""}${durationHours > 0 ? ` ${durationHours}h` : ""}`
                        : durationHours > 0
                          ? `${durationHours} heure${durationHours > 1 ? "s" : ""}`
                          : "< 1 heure";

                      const typeConfig: Record<string, { label: string; className: string }> = {
                        mission: { label: "Mission", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
                        client: { label: "Chez client", className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
                        atelier: { label: "Atelier", className: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300" },
                        pret: { label: "Prêt", className: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" },
                        sav: { label: "SAV", className: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
                      };
                      const cfg = typeConfig[loc.locationType] || { label: loc.locationType, className: "" };

                      return (
                        <div key={loc.id} className="flex items-start gap-3 rounded-lg border bg-card p-3" data-testid={`loc-entry-${loc.id}`}>
                          <div className="mt-0.5">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.className}`}>
                              {cfg.label}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0 space-y-1">
                            <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                              <span>{startDate.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</span>
                              <span>→</span>
                              {endDate ? (
                                <span>{endDate.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</span>
                              ) : (
                                <span className="text-green-600 dark:text-green-400 font-medium">En cours</span>
                              )}
                              <span className="flex items-center gap-1 text-muted-foreground/70">
                                <Clock className="w-3 h-3" />
                                {durationStr}
                              </span>
                            </div>
                            <div className="flex gap-3 flex-wrap">
                              {loc.missionId && (
                                <Link href={`/missions/${loc.missionId}`}>
                                  <span className="inline-flex items-center gap-1 text-xs text-primary hover:underline cursor-pointer">
                                    <ExternalLink className="w-3 h-3" />
                                    Voir la mission
                                  </span>
                                </Link>
                              )}
                              {loc.clientId && (
                                <Link href={`/clients/${loc.clientId}`}>
                                  <span className="inline-flex items-center gap-1 text-xs text-primary hover:underline cursor-pointer">
                                    <ExternalLink className="w-3 h-3" />
                                    Voir la fiche client
                                  </span>
                                </Link>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                </div>
              </TabsContent>

              <TabsContent value="sav" className="space-y-4 mt-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4 text-primary" />
                    Dossiers SAV
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="btn-creer-sav-from-robot"
                    onClick={() => {
                      if (!selectedRobot) return;
                      const params = new URLSearchParams({
                        prefill: '1',
                        origine: 'demo',
                        robotId: selectedRobot.id,
                        sn: selectedRobot.serialNumber || '',
                        model: selectedRobot.modelName || '',
                      });
                      setLocation(`/sav?${params.toString()}`);
                    }}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Créer dossier SAV
                  </Button>
                </div>
                {/* Bloc contexte Pudu (lecture seule) */}
                {puduContext && (
                  <div className="rounded-xl border bg-violet-50 dark:bg-violet-950/20 border-violet-200 dark:border-violet-800 p-3 space-y-1.5" data-testid="pudu-context-block">
                    <p className="text-xs font-semibold text-violet-700 dark:text-violet-400 flex items-center gap-1.5 mb-2">
                      <Bot className="w-3.5 h-3.5" />
                      Contexte Pudu Cloud
                    </p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      {puduContext.robot_type && (
                        <>
                          <span className="text-muted-foreground">Modèle Pudu</span>
                          <span className="font-medium">{puduContext.robot_type}</span>
                        </>
                      )}
                      {puduContext.shop_name && (
                        <>
                          <span className="text-muted-foreground">Shop</span>
                          <span className="font-medium">{puduContext.shop_name}</span>
                        </>
                      )}
                      {puduContext.company_name && puduContext.company_name !== puduContext.shop_name && (
                        <>
                          <span className="text-muted-foreground">Entreprise</span>
                          <span className="font-medium">{puduContext.company_name}</span>
                        </>
                      )}
                      {puduContext.last_sync && (
                        <>
                          <span className="text-muted-foreground">Dernière sync</span>
                          <span className="font-medium">
                            {new Date(puduContext.last_sync).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {savDossiersLoading ? (
                  <div className="space-y-2">
                    {[1, 2].map(i => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
                  </div>
                ) : robotSavDossiers.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground text-sm flex flex-col items-center gap-2">
                    <ShieldAlert className="w-8 h-8 text-muted-foreground/40" />
                    Aucun dossier SAV pour ce robot.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {robotSavDossiers.map((d: any) => (
                      <div key={d.id} className="flex items-center justify-between py-2 px-3 rounded-lg border bg-secondary/20 hover:bg-secondary/30 transition-colors" data-testid={`sav-dossier-${d.id}`}>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs font-semibold">{d.reference}</span>
                            <StatusBadge status={d.statut} type="sav" label={{ ouvert: "Ouvert", en_cours: "En cours", en_attente_pieces: "En attente pièces", resolu: "Résolu", cloture: "Clôturé" }[d.statut as string]} />
                          </div>
                          {d.dateCreation && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {new Date(d.dateCreation).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}
                            </p>
                          )}
                          {d.problematique && (
                            <p className="text-xs text-muted-foreground line-clamp-1">{d.problematique}</p>
                          )}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setLocation('/sav')} data-testid={`btn-open-sav-${d.id}`}>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Marquer comme arrivé */}
      <Dialog open={arriveeOpen} onOpenChange={setArriveeOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Marquer comme arrivé à l'atelier
            </DialogTitle>
            <DialogDescription>
              Enregistrez l'arrivée du robot {selectedRobot?.modelName} (SN : {selectedRobot?.serialNumber}).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Provenance / Retour de</Label>
              <Input
                placeholder="Ex: Site client Paris, Location Pudu..."
                value={arriveeData.provenance}
                onChange={(e) => setArriveeData((p) => ({ ...p, provenance: e.target.value }))}
                data-testid="input-arrivee-provenance"
              />
            </div>
            <div className="space-y-2">
              <Label>Note (optionnel)</Label>
              <Textarea
                placeholder="État du robot, remarques..."
                value={arriveeData.note}
                onChange={(e) => setArriveeData((p) => ({ ...p, note: e.target.value }))}
                rows={3}
                data-testid="input-arrivee-note"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setArriveeOpen(false)}>
                Annuler
              </Button>
              <Button
                className="flex-1"
                onClick={handleArriveeAtelier}
                disabled={arriveeLoading}
                data-testid="button-confirm-arrivee"
              >
                {arriveeLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Confirmer l'arrivée
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le robot</DialogTitle>
            <DialogDescription>Mettre à jour les informations du robot.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom du modèle</Label>
                <Input data-testid="input-edit-model" required value={editData.modelName} onChange={e => setEditData({...editData, modelName: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Modèle de produit</Label>
                <Input data-testid="input-edit-product-model" value={editData.productModel} onChange={e => setEditData({...editData, productModel: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Surnom de la machine</Label>
                <Input data-testid="input-edit-nickname" value={editData.nickname} onChange={e => setEditData({...editData, nickname: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Couleur</Label>
                <Input data-testid="input-edit-color" value={editData.color} onChange={e => setEditData({...editData, color: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SKU</Label>
                <Input data-testid="input-edit-sku" required value={editData.sku} onChange={e => setEditData({...editData, sku: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Numéro de série</Label>
                <Input required value={editData.serialNumber} onChange={e => setEditData({...editData, serialNumber: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Adresse MAC</Label>
                <Input required value={editData.macAddress} onChange={e => setEditData({...editData, macAddress: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={editData.category} onValueChange={v => setEditData({...editData, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="livraison">Livraison</SelectItem>
                    <SelectItem value="nettoyage">Nettoyage</SelectItem>
                    <SelectItem value="industriel">Industriel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={editData.robotType} onValueChange={v => setEditData({...editData, robotType: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="demo">Démo</SelectItem>
                    <SelectItem value="vendu">Vendu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select value={editData.status} onValueChange={v => setEditData({...editData, status: v})}>
                  <SelectTrigger data-testid="select-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROBOT_FLEET_ASSIGNABLE_STATUSES.map((definition) => (
                      <SelectItem key={definition.value} value={definition.value}>{definition.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {selectedRobot && (
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Photo de profil
                </Label>
                <PhotoUploadSlot
                  label="Photo de profil"
                  currentUrl={selectedRobot.profilePhotoUrl || null}
                  uploading={!!uploadingPhotos[`${selectedRobot.id}_profile`]}
                  onUpload={(file) => uploadRobotPhoto(selectedRobot.id, "profile", file)}
                  testId="upload-edit-profile-photo"
                />

                <Label className="flex items-center gap-2 mt-4">
                  <ImageIcon className="w-4 h-4" />
                  Photos d'identité (4 faces)
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {identityPhotoSlots.map(({ type, label, urlKey }) => (
                    <PhotoUploadSlot
                      key={type}
                      label={label}
                      currentUrl={selectedRobot[urlKey as keyof Robot] as string | null}
                      uploading={!!uploadingPhotos[`${selectedRobot.id}_${type}`]}
                      onUpload={(file) => uploadRobotPhoto(selectedRobot.id, type, file)}
                      testId={`upload-edit-${type}`}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={editData.notes} onChange={e => setEditData({...editData, notes: e.target.value})} rows={2} />
            </div>
            <div className="pt-4 flex justify-end gap-2">
              <Button variant="outline" type="button" onClick={() => setEditOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={updateMutation.isPending} data-testid="button-submit-edit">
                {updateMutation.isPending ? "Mise à jour..." : "Enregistrer"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer le robot <strong>{robotToDelete?.modelName}</strong>
              {robotToDelete?.nickname ? ` (${robotToDelete.nickname})` : ""} ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? "Suppression..." : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
